/**
 * Health tab: the issue list, a search box, the "Currently viewing" header
 * with the camera buttons, and the recursive subsystem card tree. Selection
 * is useUI.selection.subsystemId both ways: clicking a card selects it (and
 * again deselects), a selection made in the scene opens its ancestors and
 * scrolls the card into view.
 */
import { useEffect, useMemo } from 'react';
import { ChevronRight, Crosshair, Orbit, Search, TriangleAlert, X } from 'lucide-react';
import { Chip, IconButton, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useUI } from '@/state/store';
import { findSubsystem, useTwin } from '../../store';
import type { Subsystem } from '../../types';
import { isModelOnlyElement } from '../../data/modelParts';
import { DataValue } from '../components/DataValue';
import { ReferenceSpecs } from '../components/ReferenceSpecs';
import { IssueBadges, StatusIcon, StatusPill } from '../components/badges';
import { EmptyState, SectionTitle, inputClass } from '../components/layout';
import { useAssetName } from '../hooks/useAssetName';
import { useItemLookup, useSubsystemColour } from '../hooks/useItemLookup';
import { useNowMs } from '../hooks/useNowMs';
import { useHasHistory } from '../hooks/useReadingSeries';
import { collectIssues, filterTree, pathLabel, pathTo, rollupBadges, type TreeFilter } from '../lib/cardTree';
import { colourLabel, extractUnit, levelColour, worstColour } from '../lib/values';
import { useTwinUi } from '../state';
import { isRfims } from '@/features/rfims/registry';
import { RfimsHealthPanel } from '@/features/rfims/RfimsHealthPanel';

const MAX_ISSUES_SHOWN = 8;

export function HealthPanel() {
  const assetId = useTwin(s => s.assetId);
  return isRfims(assetId) ? <RfimsHealthPanel /> : <GeneralHealthPanel />;
}

function GeneralHealthPanel() {
  const element = useTwin((s) => s.element);
  const loadStatus = useTwin((s) => s.loadStatus);
  const loadError = useTwin((s) => s.loadError);
  const assetId = useUI((s) => s.selection.assetId);
  const search = useTwinUi((s) => s.search);
  const setSearch = useTwinUi((s) => s.setSearch);
  const lookup = useItemLookup();

  const issues = useMemo(() => (element ? collectIssues(element.subsystems, lookup.colourOf, lookup.valueOf) : []), [element, lookup]);
  const filter = useMemo(() => (element ? filterTree(element.subsystems, search) : null), [element, search]);

  if (!assetId) return <EmptyState title="No asset selected." className="m-3">Pick an antenna or a site from the strip to open its twin.</EmptyState>;
  if (loadStatus === 'error') return <EmptyState title="The twin did not load." className="m-3">{loadError}</EmptyState>;
  if (!element) return <div className="p-4 text-xs text-ink-3">Loading {assetId}.</div>;

  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      <ViewingHeader element={element} issues={issues.length} />
      <IssueList issues={issues} />
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
        <input
          type="search"
          aria-label="Search subsystems and readings"
          placeholder="Search subsystems and readings"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={cn(inputClass, 'w-full pl-8')}
        />
      </label>
      <div className="flex flex-col gap-1">
        {element.subsystems.map((s) => (
          <SubsystemCard key={s.id} subsystem={s} depth={0} filter={filter} />
        ))}
        {filter && filter.visible.size === 0 && <EmptyState title="Nothing matches." />}
      </div>
    </div>
  );
}

function ViewingHeader({ element, issues }: { element: NonNullable<ReturnType<typeof useTwin.getState>['element']>; issues: number }) {
  const selectedId = useUI((s) => s.selection.subsystemId);
  const select = useUI((s) => s.select);
  const asset = useTwin((s) => s.asset);
  const subviewId = useTwin((s) => s.subviewId);
  const setSubview = useTwin((s) => s.setSubview);
  const requestCamera = useTwin((s) => s.requestCamera);
  const statuses = useTwin((s) => s.statuses);
  const subsystemColour = useSubsystemColour();
  const lookup = useItemLookup();
  const name = useAssetName();

  const selected = selectedId ? findSubsystem(element.subsystems, selectedId) : undefined;
  const path = selected ? pathTo(element.subsystems, selected.id) : [];

  const overall = useMemo(() => {
    const colours = element.subsystems.map((s) => subsystemColour(s));
    const colour = worstColour(colours) ?? levelColour('OPERATIONAL');
    return colour;
  }, [element, subsystemColour]);

  const subviews = asset?.subviews ?? [];
  const currentBindings = subviews.find(v => v.id === subviewId)?.bindings ?? asset?.bindings;
  const partView = selected ? subviews.find(v => v.bindings?.some(b => b.subsystemId === selected.id)) : undefined;
  const hasGeometry = !selected || currentBindings?.some(b => b.subsystemId === selected.id);
  const canFrame = hasGeometry || partView || (selected && asset?.bindings.some(b => b.subsystemId === selected.id));
  const sourceOnlyPart = selected?.modelOnly || (!selected && isModelOnlyElement(element));
  const derived = selected ? statuses[selected.id] : undefined;
  const colour = selected ? (derived?.colour ?? subsystemColour(selected)) : overall;
  const label = selected ? (derived?.level ?? colourLabel(colour)) : colourLabel(colour);
  const badges = selected ? rollupBadges(selected, lookup.colourOf) : null;

  return (
    <div className="rounded-control border border-hairline bg-inset/60 px-3 py-2">
      <SectionTitle
        right={
          selected && (
            <IconButton label="Deselect" className="h-6 w-6" onClick={() => select({ subsystemId: null })}>
              <X className="h-3.5 w-3.5" />
            </IconButton>
          )
        }
      >
        {selected ? 'Currently viewing' : 'Asset'}
      </SectionTitle>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-display text-sm font-semibold text-ink" data-testid="twin-viewing-title">
            {selected ? selected.label : (name?.title ?? element.id)}
          </div>
          {selected && path.length > 1 && <div className="truncate text-[10px] text-ink-3">{pathLabel(path.slice(0, -1))}</div>}
        </div>
        {sourceOnlyPart ? <Chip tone="neutral">No telemetry</Chip> : <StatusPill colour={colour} label={label} className="shrink-0" />}
      </div>
      {(selected ? selected.description : name?.subtitle) && <p className="mt-1 leading-snug text-ink-2">{selected ? selected.description : name?.subtitle}</p>}
      {selected && !hasGeometry && <p className="mt-1 leading-snug text-ink-3">{partView ? `Modelled in ${partView.label.toLowerCase()}. Frame opens that view.` : canFrame ? 'Modelled in the site view. Frame returns to the site.' : selected.reference ? 'Instrument reference. This model does not identify a separate mesh for this component.' : 'No separate mesh is assigned to this component in the original model.'}</p>}
      {!selected && (
        <div className="mt-1 text-ink-3">
          {isModelOnlyElement(element) ? 'Reference model and specifications; no connected health readings.' : issues === 0 ? 'No readings outside their limits.' : `${issues} reading${issues === 1 ? '' : 's'} outside limits.`}
        </div>
      )}
      {badges && (badges.red > 0 || badges.yellow > 0) && (
        <div className="mt-1 flex items-center gap-2 text-ink-3">
          <IssueBadges red={badges.red} yellow={badges.yellow} />
          <span>
            {badges.red} critical, {badges.yellow} warning in this card
          </span>
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {selected?.reference && !canFrame && <TextButton onClick={() => requestCamera({ poseId: 'main' })}><Crosshair className="mr-1 inline h-3.5 w-3.5" />Frame spacecraft</TextButton>}
        {selected && canFrame && (
          <TextButton className="flex items-center gap-1" onClick={() => requestCamera({ subsystemId: selected.id })} title="Frame this subsystem in the scene">
            <Crosshair className="h-3.5 w-3.5" /> Frame
          </TextButton>
        )}
        {selected?.orbit && (
          <TextButton className="flex items-center gap-1" onClick={() => requestCamera({ poseId: selected.orbit })} title="Orbit camera">
            <Orbit className="h-3.5 w-3.5" /> Orbit
          </TextButton>
        )}
        {subviews.map((sv) => (
          <TextButton key={sv.id} active={subviewId === sv.id} onClick={() => setSubview(subviewId === sv.id ? null : sv.id)}>
            {subviewId === sv.id ? 'Back to main' : sv.label}
          </TextButton>
        ))}
        {!selected && !subviewId && (
          <TextButton className="flex items-center gap-1" onClick={() => requestCamera({ poseId: 'main' })}>
            <Crosshair className="h-3.5 w-3.5" /> Frame asset
          </TextButton>
        )}
      </div>
    </div>
  );
}

function IssueList({ issues }: { issues: ReturnType<typeof collectIssues> }) {
  const select = useUI((s) => s.select);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const setReading = useTwinUi((s) => s.setReading);
  const element = useTwin((s) => s.element);
  if (issues.length === 0) return null;
  const shown = issues.slice(0, MAX_ISSUES_SHOWN);
  const jump = (subsystemId: string, dataId: string) => {
    if (!element) return;
    setExpanded(pathTo(element.subsystems, subsystemId).map((s) => s.id), true);
    select({ subsystemId });
    setReading({ subsystemId, dataId });
  };
  return (
    <div>
      <SectionTitle right={<span className="font-mono text-[10px] text-ink-3">{issues.length}</span>}>
        <span className="inline-flex items-center gap-1">
          <TriangleAlert className="h-3 w-3 text-caution" /> Issues
        </span>
      </SectionTitle>
      <ul className="flex flex-col gap-0.5">
        {shown.map((i) => (
          <li key={`${i.subsystemId}.${i.dataId}`}>
            <button
              type="button"
              onClick={() => jump(i.subsystemId, i.dataId)}
              className={cn(
                'flex w-full items-center gap-2 rounded-control border px-2 py-1 text-left transition hover:bg-overlay',
                i.colour === 'red' ? 'border-warning/30 bg-warning/5' : 'border-caution/30 bg-caution/5',
              )}
            >
              <StatusIcon colour={i.colour} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ink">
                  {i.label} <span className="font-mono text-ink-2">{i.value}</span>
                </span>
                <span className="block truncate text-[10px] text-ink-3">{i.path}</span>
              </span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-3" />
            </button>
          </li>
        ))}
        {issues.length > shown.length && <li className="px-2 text-[10px] text-ink-3">and {issues.length - shown.length} more below</li>}
      </ul>
    </div>
  );
}

function SubsystemCard({ subsystem, depth, filter }: { subsystem: Subsystem; depth: number; filter: TreeFilter | null }) {
  const selectedId = useUI((s) => s.selection.subsystemId);
  const select = useUI((s) => s.select);
  const hovered = useTwin((s) => s.hoveredSubsystemId);
  const setHovered = useTwin((s) => s.setHovered);
  const derived = useTwin((s) => s.statuses[subsystem.id]);
  const expandedState = useTwinUi((s) => s.expanded[subsystem.id]);
  const toggleExpanded = useTwinUi((s) => s.toggleExpanded);
  const setExpanded = useTwinUi((s) => s.setExpanded);
  const setReading = useTwinUi((s) => s.setReading);
  const setTab = useTwinUi((s) => s.setTab);
  const element = useTwin((s) => s.element);
  const simIntervalMs = useTwin((s) => s.simIntervalMs);
  const lookup = useItemLookup();
  const subsystemColour = useSubsystemColour();
  const hasHistory = useHasHistory();
  const now = useNowMs();

  const selected = selectedId === subsystem.id;
  const expanded = filter ? filter.expand.has(subsystem.id) || expandedState === true : expandedState === true;
  const badges = useMemo(() => rollupBadges(subsystem, lookup.colourOf), [subsystem, lookup]);
  const colour = derived?.colour ?? subsystemColour(subsystem);
  const hasInner = !!subsystem.reference || (subsystem.data?.length ?? 0) > 0 || (subsystem.childs?.length ?? 0) > 0;

  // A selection made elsewhere (scene, report, GAIA) opens the ancestors and scrolls here.
  useEffect(() => {
    if (!selected || !element) return;
    setExpanded(pathTo(element.subsystems, subsystem.id).map((s) => s.id), true);
    const el = document.getElementById(`twin-card-${subsystem.id}`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected, element, subsystem.id, setExpanded]);

  if (filter && !filter.visible.has(subsystem.id)) return null;

  const onHeaderClick = () => {
    if (selected) select({ subsystemId: null });
    else {
      select({ subsystemId: subsystem.id });
      if (!expanded) toggleExpanded(subsystem.id);
    }
  };

  return (
    <div
      id={`twin-card-${subsystem.id}`}
      className={cn(
        'rounded-control border transition',
        selected ? 'border-accent/60 bg-accent-soft/40' : hovered === subsystem.id ? 'border-hairline-strong bg-overlay/60' : 'border-hairline bg-raised',
      )}
      style={{ marginLeft: depth ? 8 : 0 }}
      onMouseEnter={() => setHovered(subsystem.id)}
      onMouseLeave={() => setHovered(null)}
    >
      <div className="flex items-center gap-1 px-1.5 py-1">
        <button
          type="button"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          aria-expanded={expanded}
          disabled={!hasInner}
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(subsystem.id);
          }}
          className="rounded-control p-0.5 text-ink-3 hover:bg-overlay hover:text-ink disabled:opacity-30"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-90')} />
        </button>
        <button type="button" onClick={onHeaderClick} aria-pressed={selected} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {subsystem.modelOnly ? <span className="h-3 w-3 rounded-full border border-ink-3" aria-label="No telemetry" /> : <StatusIcon colour={colour} />}
          <span className={cn('min-w-0 flex-1 truncate', selected ? 'text-ink' : 'text-ink-2')}>{subsystem.label}</span>
          <IssueBadges red={badges.red} yellow={badges.yellow} />
          {subsystem.modelOnly ? <Chip tone="neutral" className="px-1.5 py-0 text-[9px]">{subsystem.reference ? 'SPEC' : 'MODEL'}</Chip> : derived && (
            <Chip tone={colour === 'green' ? 'nominal' : colour === 'yellow' ? 'caution' : 'warning'} className="hidden px-1.5 py-0 text-[9px] sm:inline-flex">
              {derived.level}
            </Chip>
          )}
        </button>
      </div>
      {expanded && hasInner && (
        <div className="border-t border-hairline">
          <ReferenceSpecs subsystem={subsystem} />
          {subsystem.data?.map((item) => {
            const v = lookup.view(subsystem.id, item);
            const ref = { subsystemId: subsystem.id, dataId: item.id };
            const key = `${subsystem.id}.${item.id}`;
            return (
              <DataValue
                key={item.id}
                label={item.label}
                value={v.value}
                unit={item.normalRange?.unit?.trim() || extractUnit(item.value) || undefined}
                colour={v.colour}
                source={v.source}
                ageMs={v.timeMs !== null ? (v.freshnessMs ?? Math.max(0, now - v.timeMs)) : undefined}
                freshMs={v.source === 'sim' ? simIntervalMs * 2 : 60_000}
                description={item.description}
                highlighted={filter?.items.has(key)}
                onChart={
                  hasHistory(ref)
                    ? () => {
                        setReading(ref);
                        select({ subsystemId: subsystem.id });
                        setTab('readings');
                      }
                    : undefined
                }
              />
            );
          })}
          {subsystem.childs && subsystem.childs.length > 0 && (
            <div className="flex flex-col gap-1 p-1.5">
              {subsystem.childs.map((c) => (
                <SubsystemCard key={c.id} subsystem={c} depth={depth + 1} filter={filter} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
